import { FullStory } from '@fullstory/browser'
import { initCapture, type CaptureController, type FullStoryUser } from '@replayio/self-healing-capture'

let capture: CaptureController | undefined
let fullStoryInitialized = false

/** The captured-session replay harness sets this in-page flag (as a browser-context init
 * script, before any application script runs) to mark a reproduction run; see
 * src/guidance/user-session/session-network-replay.ts install(). */
interface ReplayWindow extends Window {
  __replayQaSessionNetwork?: boolean
}

/** Identify only after initialization; previews inherit configuration without starting capture. */
export function identifyFullStoryUser(user: FullStoryUser | null): void {
  capture?.identify(user)
}

/**
 * Name the surface the user is on. Without this call FullStory knows only the raw URL: a "Page" it can
 * count, segment or search on comes either from a URL rule configured in its UI or from here, which is
 * why SPA routes that are plainly visible in a session replay return nothing when searched.
 *
 * FullStory ends a page only when the URL host or path changes, so `pageName` must be stable for a
 * given path — changing it within one page is rejected as a `pageNameChanged` error (visible under
 * Settings > Integrations > API Errors). Anything that varies WITHOUT a path change (a query param, a
 * modal tab) belongs in `properties`, which merge across repeated calls on the same page.
 */
export function setFullStoryPage(pageName: string, properties: Record<string, unknown> = {}): void {
  if (!fullStoryInitialized) return
  try {
    FullStory('setProperties', { type: 'page', properties: { pageName, ...properties } })
  } catch {
    // Telemetry must never throw into the app.
  }
}

/**
 * A domain event FullStory can search, segment and build funnels on. Product code doesn't call this
 * directly — trackEvent() in lib/analytics.ts forwards named events here so one call site feeds both
 * our own analytics and FullStory.
 */
export function trackFullStoryEvent(name: string, properties: Record<string, unknown> = {}): void {
  if (!fullStoryInitialized) return
  try {
    FullStory('trackEvent', { name, properties })
  } catch {
    // Telemetry must never throw into the app.
  }
}

/** Use the shared producer for QA's own supplemental session recording. */
export function initFullStory(): void {
  // A reproduction run must not install the capture fetch wrapper: the wrapper re-issues every
  // fetch as a single Request and reads the request body in parallel (request.clone()
  // .arrayBuffer()) before dispatch, and that parallel read consumes the body on the
  // recording-browser builds the replay harness drives, so the harness's request interceptor
  // cannot read any POST body — every write would be body-blind to the replay's captured-response
  // and network-rule matching and answer from a synthetic error the recorded app never showed.
  // The harness records the reproduction itself (rrweb plus its injected network capture), so
  // this telemetry adds nothing a reproduction run needs.
  if ((window as ReplayWindow).__replayQaSessionNetwork === true) return
  capture = initCapture({ publishableKey: 'lqc_fEEJYFhmUuT7Vu6YlZeHChFRrqjxoyE0oCneBeemB9Q' })
  fullStoryInitialized = true
}
